import clsx from "clsx";
import { Fragment, useState, type ReactNode } from "react";
import type { View } from "../../../../../common/types.ts";
import { RosterComposition } from "../../components/RosterComposition.tsx";
import { TeamAbbrevText } from "../../components/TeamAbbrevLink.tsx";
import { ContractAmount, ContractExp } from "../../components/contract.tsx";
import { DraftAbbrev } from "../../components/DraftAbbrev.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { DraftPhaseNav } from "./DraftPhaseNav.tsx";
import { MobileP3EventFeedback } from "../../mobile/MobileP3EventFeedback.tsx";
import { PlayerFaceThumb } from "../../components/PlayerFaceThumb.tsx";
import { PlayerNameLabels } from "../../components/PlayerNameLabels.tsx";
import { MobileContentHero, MobileExpertDisclosure } from "../../mobile/mobilePrimitives.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { confirm } from "../../util/confirm.tsx";
import { decisionConfirmVariants } from "../../util/decisionConfirmVariants.ts";
import { helpers } from "../../util/helpers.ts";
import i18n from "../../util/i18n.ts";
import { formatPlayerName } from "../../util/playerNames.ts";
import { PositionText, translatePosition } from "../../util/positionNames.tsx";
import { toWorker } from "../../util/toWorker.ts";
import {
  draftDecisionCompareLimit,
  getDraftPrimaryAction,
  getDraftProspectPreview,
  getDraftProspectRisk,
  getDraftTeamNeedSummary,
  type DraftProspectRisk,
} from "./draftDecisionState.ts";
import { StickyDraftInfo } from "./StickyDraftInfo.tsx";
import { MarketRatingStatusMetric } from "../MarketRatingStatusMetric.tsx";
import { getDraftRiskMetricScore } from "../marketEvaluationMetrics.ts";

type DraftPlayer = View<"draft">["undrafted"][number];
type DraftedPlayer = View<"draft">["drafted"][number];

const draftedResultsPreviewLimit = 3;

const getRiskLabel = (risk: DraftProspectRisk) => {
  switch (risk) {
    case "hidden":
      return i18n.t("draft.riskHidden", {
        ns: "ui",
        defaultValue: "Ratings hidden",
      });
    case "high":
      return i18n.t("draft.riskHigh", { ns: "ui", defaultValue: "High variance" });
    case "medium":
      return i18n.t("draft.riskMedium", { ns: "ui", defaultValue: "Medium risk" });
    default:
      return i18n.t("draft.riskLow", { ns: "ui", defaultValue: "Low risk" });
  }
};

const getScoutingConfidenceLabel = (risk: DraftProspectRisk) => {
  switch (risk) {
    case "hidden":
      return i18n.t("draft.scoutingConfidenceHidden", {
        ns: "ui",
        defaultValue: "Scouting confidence hidden",
      });
    case "high":
      return i18n.t("draft.scoutingConfidenceLow", {
        ns: "ui",
        defaultValue: "High upside, lower certainty",
      });
    case "medium":
      return i18n.t("draft.scoutingConfidenceMedium", {
        ns: "ui",
        defaultValue: "Moderate certainty",
      });
    default:
      return i18n.t("draft.scoutingConfidenceHigh", {
        ns: "ui",
        defaultValue: "High certainty",
      });
  }
};

const DraftPickLabel = ({
  pick,
  teamsByTid,
}: {
  pick: DraftedPlayer["draft"];
  teamsByTid: View<"draft">["teamsByTid"];
}) => (
  <>
    {i18n.t("draft.pickLabel", {
      ns: "ui",
      round: pick.round,
      pick: pick.pick,
      defaultValue: "Round {{round}}, Pick {{pick}}",
    })}
    {" · "}
    <TeamAbbrevText tid={pick.tid} abbrev={teamsByTid[pick.tid]?.abbrev ?? `${pick.tid}`} />
  </>
);

const ProspectDecisionCard = ({
  challengeNoRatings,
  drafting,
  draftUser,
  expansionDraft,
  fantasyDraft,
  player,
  spectator,
  stats,
  teamNeedPositions,
  usersTurn,
}: {
  challengeNoRatings: boolean;
  drafting: boolean;
  draftUser: (player: DraftPlayer, simToNextUserPick?: boolean) => Promise<void>;
  expansionDraft: boolean;
  fantasyDraft: boolean;
  player: DraftPlayer;
  spectator: boolean;
  stats: string[];
  teamNeedPositions: string[];
  usersTurn: boolean;
}) => {
  const risk = getDraftProspectRisk({
    challengeNoRatings,
    ovr: player.ratings.ovr,
    pot: player.ratings.pot,
  });
  const positionLabel = translatePosition(player.ratings.pos);
  const needFit = teamNeedPositions.includes(player.ratings.pos);
  const fitLabel = needFit
    ? i18n.t("draft.needFit", {
        ns: "ui",
        defaultValue: "{{position}} need fit",
        position: positionLabel,
      })
    : i18n.t("draft.depthFit", {
        ns: "ui",
        defaultValue: "{{position}} depth option",
        position: positionLabel,
      });
  return (
    <li
      className="list-group-item draft-prospect-card"
      data-draft-candidate-compare-card=""
      data-draft-prospect-card=""
      data-draft-prospect-fit={needFit ? "need" : "depth"}
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="min-width-0">
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-secondary">#{player.rank}</span>
            <div className="fw-semibold">
              <PlayerNameLabels
                pid={player.pid}
                injury={player.injury}
                skills={player.ratings.skills}
                defaultWatch={player.watch}
                firstName={player.firstName}
                firstNameShort={player.firstNameShort}
                lastName={player.lastName}
                nameOnly
              />
            </div>
          </div>
          <div className="text-body-secondary small mt-1">
            <PositionText pos={player.ratings.pos} /> ·{" "}
            {i18n.t("draft.yearsOld", {
              ns: "ui",
              age: player.age,
              defaultValue: "{{age}} years old",
            })}
          </div>
        </div>
        {spectator ? null : (
          <button
            aria-label={i18n.t("draft.reviewPickTitle", {
              ns: "ui",
              defaultValue: "Review pick",
            })}
            className="btn btn-outline-primary btn-sm"
            data-draft-pick-confirmation-dialog=""
            disabled={!usersTurn || drafting}
            onClick={() => draftUser(player)}
            type="button"
          >
            {i18n.t("draft.reviewPick", { ns: "ui", defaultValue: "Review pick" })}
          </button>
        )}
      </div>
      <div className="draft-prospect-metrics mt-2" data-draft-candidate-compare-metrics="">
        <span data-draft-fit-reason="">
          <span className="text-body-secondary">
            {i18n.t("draft.fit", { ns: "ui", defaultValue: "Fit" })}
          </span>
          <strong>{fitLabel}</strong>
        </span>
        <MarketRatingStatusMetric
          hidden={challengeNoRatings}
          label={i18n.t("draft.readyNow", { ns: "ui", defaultValue: "Ready" })}
          rating={player.ratings.ovr}
        />
        <MarketRatingStatusMetric
          hidden={challengeNoRatings}
          kind="potential"
          label={i18n.t("draft.upside", { ns: "ui", defaultValue: "Upside" })}
          rating={player.ratings.pot}
        />
        <span data-draft-role-impact="">
          <span className="text-body-secondary">
            {i18n.t("draft.expectedRole", { ns: "ui", defaultValue: "Role" })}
          </span>
          <strong>
            {needFit
              ? i18n.t("draft.expectedNeedRole", {
                  ns: "ui",
                  defaultValue: "Need patch",
                })
              : i18n.t("draft.expectedDepthRole", {
                  ns: "ui",
                  defaultValue: "Depth bet",
                })}
          </strong>
        </span>
        <div data-draft-risk-flag={risk}>
          <MobileStatusMetric
            label={i18n.t("draft.risk", { ns: "ui", defaultValue: "Risk" })}
            score={getDraftRiskMetricScore(risk)}
            value={getRiskLabel(risk)}
          />
        </div>
      </div>
      <div className="text-body-secondary small mt-2" data-draft-scouting-confidence={risk}>
        {getScoutingConfidenceLabel(risk)}
      </div>
      {fantasyDraft || expansionDraft ? (
        <div className="d-flex flex-wrap gap-2 mt-2 small">
          <span>
            <ContractAmount p={player} /> / <ContractExp p={player} />
          </span>
          {stats.map((stat) => (
            <span key={stat}>
              {helpers.roundStat(player.stats[stat], stat)} {stat}
            </span>
          ))}
        </div>
      ) : null}
      {spectator || !usersTurn ? null : (
        <div className="d-flex justify-content-end mt-2">
          <button
            className="btn btn-light-bordered btn-sm"
            data-draft-pick-confirmation-dialog=""
            data-pick-confirmation-impact=""
            disabled={drafting}
            onClick={() => draftUser(player, true)}
            type="button"
          >
            {i18n.t("draft.reviewAndSim", {
              ns: "ui",
              defaultValue: "Review and sim",
            })}
          </button>
        </div>
      )}
    </li>
  );
};

const DraftedResultCard = ({
  challengeNoRatings,
  player,
  season,
  teamsByTid,
}: {
  challengeNoRatings: boolean;
  player: DraftedPlayer;
  season: number;
  teamsByTid: View<"draft">["teamsByTid"];
}) => {
  const isPending = player.pid < 0;

  return (
    <li
      className={clsx("list-group-item", {
        "list-group-item-info": isPending,
      })}
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <div className="text-body-secondary small">
            <DraftPickLabel pick={player.draft} teamsByTid={teamsByTid} />
          </div>
          <div className="fw-semibold">
            {isPending ? (
              i18n.t("draft.pickPending", {
                ns: "ui",
                defaultValue: "Pick pending",
              })
            ) : (
              <PlayerNameLabels
                pid={player.pid}
                injury={player.injury}
                skills={player.ratings.skills}
                defaultWatch={player.watch}
                firstName={player.firstName}
                firstNameShort={player.firstNameShort}
                lastName={player.lastName}
                nameOnly
              />
            )}
          </div>
        </div>
        <span className="text-body-secondary small">
          <DraftAbbrev
            originalTid={player.draft.originalTid}
            tid={player.draft.tid}
            originalT={teamsByTid[player.draft.originalTid]}
            t={teamsByTid[player.draft.tid]}
          />
        </span>
      </div>
      {isPending ? null : (
        <div className="text-body-secondary small mt-1">
          <PositionText pos={player.ratings.pos} /> ·{" "}
          {i18n.t("draft.yearsOld", {
            ns: "ui",
            age: player.age,
            defaultValue: "{{age}} years old",
          })}
          {" · "}
          {i18n.t("draft.seasonResult", {
            ns: "ui",
            season,
            defaultValue: "{{season}} result",
          })}
        </div>
      )}
      {isPending ? null : (
        <MarketRatingStatusMetric
          className="mt-2"
          hidden={challengeNoRatings}
          label={i18n.t("marketMetrics.playerOutlook", {
            ns: "ui",
            defaultValue: "Player outlook",
          })}
          potential={player.ratings.pot}
          rating={player.ratings.ovr}
        />
      )}
    </li>
  );
};

const Draft = ({
  challengeNoDraftPicks,
  challengeNoRatings,
  drafted,
  expansionDraft,
  expansionDraftFilteredTeamsMessage,
  fantasyDraft,
  phase,
  season,
  spectator,
  stats,
  teamsByTid,
  undrafted,
  userNextPickYear,
  userPlayers,
  userTid,
  userTids,
}: View<"draft">) => {
  const [drafting, setDrafting] = useState(false);

  const draftedSorted = drafted;
  const remainingPicks = draftedSorted.filter((p) => p.pid < 0);
  const nextPick = remainingPicks[0];
  const draftIsOver = remainingPicks.length === 0;
  const usersTurn = !!(nextPick && userTids.includes(nextPick.draft.tid));
  const nextUserPickIndex = draftedSorted.findIndex(
    (p) => p.pid < 0 && userTids.includes(p.draft.tid),
  );
  const currentPickIndex = draftedSorted.findIndex((p) => p.pid < 0);
  const userRemaining = nextUserPickIndex !== -1;
  const primaryAction = getDraftPrimaryAction({
    draftIsOver,
    spectator,
    userRemaining,
    usersTurn,
  });
  const prospectPreview = getDraftProspectPreview(undrafted, draftDecisionCompareLimit);
  const topProspects = prospectPreview.visible;
  const bestProspect = topProspects[0];
  const alternativeProspects = topProspects.slice(1, draftDecisionCompareLimit);
  const teamNeedSummary = getDraftTeamNeedSummary({
    prospects: undrafted,
    rosterPlayers: userPlayers,
  });
  const draftedPlayers = draftedSorted.filter((p) => p.pid >= 0);
  const previousDraftedPlayer = draftedPlayers.at(-1);
  const draftMode = fantasyDraft
    ? "fantasy"
    : expansionDraft
      ? "expansion"
      : spectator
        ? "spectator"
        : usersTurn
          ? "user-turn"
          : userRemaining
            ? "waiting"
            : "complete";
  const renderTeamNeedBadges = () =>
    teamNeedSummary.positions.length > 0 ? (
      // Need chips double as the entry into scouting that position (content +
      // action, 法则一) instead of plain narration badges.
      teamNeedSummary.positions.map((pos) => (
        <a
          className="badge text-bg-warning text-decoration-none"
          data-draft-team-need=""
          href={helpers.leagueUrl(["draft_scouting"])}
          key={pos}
        >
          {i18n.t("draft.needPosition", {
            ns: "ui",
            defaultValue: "Need {{position}}",
            position: translatePosition(pos),
          })}
        </a>
      ))
    ) : (
      <span className="badge text-bg-success" data-draft-team-need-balanced="">
        {i18n.t("draft.needsBalanced", {
          ns: "ui",
          defaultValue: "Needs balanced",
        })}
      </span>
    );

  const draftUser = async (player: DraftPlayer, simToNextUserPick = false) => {
    const playerName = formatPlayerName(player.firstName, player.lastName, player.firstNameShort);
    const risk = getDraftProspectRisk({
      challengeNoRatings,
      ovr: player.ratings.ovr,
      pot: player.ratings.pot,
    });
    const ratingSummary = challengeNoRatings
      ? i18n.t("draft.hiddenRatings", {
          ns: "ui",
          defaultValue: "Ratings hidden",
        })
      : i18n.t("draft.ovrPot", {
          ns: "ui",
          ovr: player.ratings.ovr,
          pot: player.ratings.pot,
          defaultValue: "{{ovr}}/{{pot}} Overall/Potential",
        });
    const proceed = await confirm(
      i18n.t("draft.confirmPick", {
        ns: "ui",
        defaultValue:
          "Draft {{playerName}}? {{position}}, {{ratings}}, {{risk}}. This commits the pick and updates your roster.",
        playerName,
        position: translatePosition(player.ratings.pos, "full"),
        ratings: ratingSummary,
        risk: getRiskLabel(risk),
      }),
      {
        okText: i18n.t("draft.confirmPickOk", {
          ns: "ui",
          defaultValue: "Confirm pick",
        }),
        cancelText: i18n.t("common.cancel", {
          ns: "ui",
          defaultValue: "Cancel",
        }),
        variant: "draft-pick",
      },
    );

    if (!proceed) {
      return;
    }

    try {
      setDrafting(true);
      await toWorker("main", "draftUser", player.pid);
    } finally {
      setDrafting(false);
    }

    if (simToNextUserPick) {
      await toWorker("playMenu", "untilYourNextPick", undefined);
    }
  };

  const simToCurrentPick = async () => {
    if (!nextPick) {
      return;
    }

    if (!spectator) {
      const numUserPicksBefore = draftedSorted
        .slice(0, currentPickIndex)
        .filter((p) => p.pid < 0 && userTids.includes(p.draft.tid)).length;

      if (numUserPicksBefore > 0) {
        const proceed = await confirm(
          i18n.t("draft.simToPickWarning", {
            ns: "ui",
            defaultValue:
              "Your {{teamControl}} {{count}} {{picks}} before this one. The AI will make {{draftPicks}} for you if you choose to sim to this pick.",
            teamControl: helpers.plural("team controls", userTids.length, "teams control"),
            count: numUserPicksBefore,
            picks: helpers.plural("pick", numUserPicksBefore),
            draftPicks: helpers.plural("that draft pick", numUserPicksBefore, "those draft picks"),
          }),
          {
            okText: i18n.t("draft.letAiMakePick", {
              ns: "ui",
              defaultValue: "Let AI Make My {{picks}}",
              picks: helpers.plural("Pick", numUserPicksBefore),
            }),
            cancelText: i18n.t("common.cancel", {
              ns: "ui",
              defaultValue: "Cancel",
            }),
            variant: decisionConfirmVariants.draftSimToPick,
          },
        );

        if (!proceed) {
          return;
        }
      }
    }

    await toWorker("actions", "untilPick", nextPick.draft.dpid);
  };

  const simToNextUserPick = async () => {
    await toWorker("playMenu", "untilYourNextPick", undefined);
  };

  const simOnePick = async () => {
    await toWorker("playMenu", "onePick", undefined);
  };

  const finishDraft = async () => {
    if (userRemaining && !spectator) {
      const result = await confirm(
        i18n.t("draft.aiFinishConfirm", {
          ns: "ui",
          defaultValue:
            "If you proceed, the AI will make your remaining picks for you. Are you sure?",
        }),
        {
          okText: i18n.t("draft.letAiFinish", {
            ns: "ui",
            defaultValue: "Let AI finish the draft",
          }),
          cancelText: i18n.t("common.cancel", {
            ns: "ui",
            defaultValue: "Cancel",
          }),
          variant: decisionConfirmVariants.draftAiFinish,
        },
      );

      if (!result) {
        return;
      }
    }

    await toWorker("playMenu", "untilEnd", undefined);
  };

  useTitleBar({
    title: fantasyDraft
      ? i18n.t("fantasyDraft.title", {
          ns: "ui",
          defaultValue: "Fantasy Draft",
        })
      : expansionDraft
        ? i18n.t("expansionDraft.title", {
            ns: "ui",
            defaultValue: "Expansion Draft",
          })
        : i18n.t("draft.title", { ns: "ui", defaultValue: "Draft" }),
  });

  const messages: ReactNode[] = [];
  if (remainingPicks.length > 0) {
    if (challengeNoDraftPicks && !fantasyDraft && !expansionDraft) {
      messages.push(
        <div>
          <p className="alert alert-danger d-inline-block">
            <b>
              {i18n.t("common.challengeMode", {
                ns: "ui",
                defaultValue: "Challenge Mode:",
              })}
            </b>{" "}
            {i18n.t("draft.challengeNoDraftPicks", {
              ns: "ui",
              defaultValue:
                "Your team does not get any draft picks unless you acquire them in a trade.",
            })}
          </p>
        </div>,
      );
    }
    if (spectator) {
      messages.push(
        <div>
          <p className="alert alert-danger d-inline-block" data-draft-spectator-warning="">
            {i18n.t("draft.spectatorMode", {
              ns: "ui",
              defaultValue:
                "In spectator mode you can't make draft picks, you can only watch the draft.",
            })}
          </p>
        </div>,
      );
    }
    if (expansionDraftFilteredTeamsMessage) {
      messages.push(
        <div>
          <p className="alert alert-warning d-inline-block">{expansionDraftFilteredTeamsMessage}</p>
        </div>,
      );
    }
  } else {
    messages.push(
      <p>
        <span className="alert alert-success d-inline-block mb-0">
          {i18n.t("draft.draftOver", {
            ns: "ui",
            defaultValue: "The draft is over!",
          })}
        </span>
      </p>,
    );
    if (fantasyDraft || expansionDraft) {
      messages.push(
        <p>
          <span className="alert alert-warning d-inline-block mb-0">
            {i18n.t("draft.temporaryResults", {
              ns: "ui",
              defaultValue:
                "Draft results from {{type}} drafts are only temporarily viewable. When you navigate away from this page or proceed to the next phase of the game, you cannot come back to this page.",
              type: fantasyDraft ? "fantasy" : "expansion",
            })}
          </span>
        </p>,
      );
    }
  }

  // B2c (v5Content): the single primary CTA follows the phase task; every other
  // sim/trade action lives in the one overflow disclosure below (法则一/A4).
  const primaryCta =
    primaryAction === "chooseFromShortlist" && bestProspect ? (
      <button
        className="btn btn-primary w-100"
        data-draft-pick-confirmation-dialog=""
        data-draft-candidate-pick-action=""
        data-draft-primary-action=""
        data-pick-confirmation-impact=""
        disabled={!usersTurn || drafting || spectator}
        onClick={() => draftUser(bestProspect)}
        type="button"
      >
        {i18n.t("draft.p0s09SelectRecommended", {
          ns: "ui",
          defaultValue: "Select recommended player",
        })}
      </button>
    ) : primaryAction === "toNextUserPick" ? (
      <button
        className="btn btn-primary w-100"
        data-draft-primary-action=""
        data-draft-to-next-user-pick-action="primary"
        disabled={!userRemaining || drafting}
        onClick={simToNextUserPick}
        type="button"
      >
        {i18n.t("draft.toYourNextPick", {
          ns: "ui",
          defaultValue: "To your next pick",
        })}
      </button>
    ) : primaryAction === "finishDraft" ? (
      <button
        className="btn btn-primary w-100"
        data-draft-ai-finish-action="primary"
        data-draft-ai-finish-command="untilEnd"
        data-draft-ai-finish-confirm-required={userRemaining && !spectator ? "true" : "false"}
        data-draft-primary-action=""
        disabled={drafting}
        onClick={finishDraft}
        type="button"
      >
        {i18n.t("draft.toEndOfDraft", {
          ns: "ui",
          defaultValue: "To end of draft",
        })}
      </button>
    ) : null;

  return (
    <div
      data-draft-production-mode={draftMode}
      data-draft-production-spectator={spectator ? "true" : "false"}
      data-draft-production-waiting={
        !fantasyDraft && !expansionDraft && !spectator && !usersTurn && userRemaining
          ? "true"
          : "false"
      }
      data-draft-rendered-current-pick-number={nextPick?.draft.pick}
      data-draft-rendered-current-pick-round={nextPick?.draft.round}
      data-draft-rendered-current-pick-tid={nextPick?.draft.tid}
      data-draft-rendered-drafted-count={draftedPlayers.length}
      data-draft-rendered-drafted-pids={draftedPlayers.map((player) => player.pid).join(",")}
      data-draft-rendered-phase={phase}
      data-draft-rendered-previous-pick-pid={previousDraftedPlayer?.pid}
      data-draft-rendered-previous-pick-skills={JSON.stringify(
        previousDraftedPlayer?.ratings.skills ?? [],
      )}
      data-v5-draft-content-first-screen=""
    >
      {/* G2d-2: phase-driven draft-domain nav replaces the retired hub tab
          strip (hidden for fantasy/expansion drafts, which have no sibling
          scouting/lottery/history views). */}
      {!fantasyDraft && !expansionDraft ? <DraftPhaseNav draftIsOver={draftIsOver} /> : null}

      <div>
        <StickyDraftInfo
          challengeNoRatings={challengeNoRatings}
          drafted={draftedSorted}
          season={season}
          spectator={spectator}
          userNextPickYear={userNextPickYear}
          userTids={userTids}
        />
      </div>

      {!draftIsOver && bestProspect
        ? (() => {
            const risk = getDraftProspectRisk({
              challengeNoRatings,
              ovr: bestProspect.ratings.ovr,
              pot: bestProspect.ratings.pot,
            });

            // B2c (v5Content): the first screen IS the pick decision — current
            // pick status stays in the sticky strip above, the hero children are
            // the team-need badges, the #1 recommendation expanded card, and the
            // #2/#3 compact rows. The clock-hero narration card (page heading,
            // compare instruction, safe-advance conditions, confirmation-impact
            // sentence) is gone; guidance is the one explainer line (法则一).
            return (
              <MobileContentHero
                action={primaryCta ? <div className="d-grid">{primaryCta}</div> : undefined}
                className="mb-3 draft-clock-hero"
                data-candidate-compare-cards=""
                data-draft-candidate-compare-count={topProspects.length}
                data-draft-clock-hero=""
                data-draft-current-decision=""
                data-draft-current-pick-number={nextPick.draft.pick}
                data-draft-current-pick-round={nextPick.draft.round}
                data-draft-current-pick-tid={nextPick.draft.tid}
                data-draft-current-pick-hero=""
                data-draft-primary-action-kind={primaryAction}
                data-draft-prospect-shortlist=""
                data-v5-draft-pick-decision-first-screen=""
                explainer={
                  // One guidance line only while the pick is actually yours;
                  // while waiting, the sticky status strip is enough context.
                  usersTurn ? (
                    <span data-draft-safe-advance-conditions="" data-team-need-recommendation="">
                      {i18n.t("draft.teamNeedRecommendation", {
                        ns: "ui",
                        defaultValue:
                          "Compare the shortlist against your roster needs before confirming the pick.",
                      })}
                    </span>
                  ) : undefined
                }
                id="draft-prospect-shortlist"
              >
                <div className="d-flex flex-wrap gap-2" data-draft-team-needs="">
                  {renderTeamNeedBadges()}
                </div>

                <div className="border rounded p-2 mt-3 draft-pick-decision-card">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    {/* C2: face thumbnail for the #1 recommendation (lazy +
                        cached inside PlayerFaceThumb). */}
                    <PlayerFaceThumb
                      face={bestProspect.face}
                      imgURL={bestProspect.imgURL}
                      pid={bestProspect.pid}
                      size={48}
                    />
                    <div className="min-width-0 flex-grow-1">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary">#{bestProspect.rank}</span>
                        <div className="fw-semibold">
                          <PlayerNameLabels
                            pid={bestProspect.pid}
                            injury={bestProspect.injury}
                            skills={bestProspect.ratings.skills}
                            defaultWatch={bestProspect.watch}
                            firstName={bestProspect.firstName}
                            firstNameShort={bestProspect.firstNameShort}
                            lastName={bestProspect.lastName}
                            nameOnly
                          />
                        </div>
                      </div>
                      {/* 法则二: Ready/Upside below carry the ratings, so the
                          subtitle stays position + age only; the need badges
                          above already carry the fit signal. */}
                      <div className="text-body-secondary small mt-1">
                        <PositionText pos={bestProspect.ratings.pos} /> ·{" "}
                        {i18n.t("draft.yearsOld", {
                          ns: "ui",
                          age: bestProspect.age,
                          defaultValue: "{{age}} years old",
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="draft-prospect-metrics mt-3">
                    <MarketRatingStatusMetric
                      hidden={challengeNoRatings}
                      label={i18n.t("draft.readyNow", { ns: "ui", defaultValue: "Ready" })}
                      rating={bestProspect.ratings.ovr}
                    />
                    <MarketRatingStatusMetric
                      hidden={challengeNoRatings}
                      kind="potential"
                      label={i18n.t("draft.upside", { ns: "ui", defaultValue: "Upside" })}
                      rating={bestProspect.ratings.pot}
                    />
                    <div
                      data-draft-risk-flag={risk}
                      data-draft-scouting-confidence={risk}
                    >
                      <MobileStatusMetric
                        label={i18n.t("draft.risk", {
                          ns: "ui",
                          defaultValue: "Risk",
                        })}
                        score={getDraftRiskMetricScore(risk)}
                        value={getRiskLabel(risk)}
                      />
                    </div>
                  </div>
                </div>

                <div className="d-grid gap-2 mt-2">
                  {alternativeProspects.map((player) => {
                    const alternativeRisk = getDraftProspectRisk({
                      challengeNoRatings,
                      ovr: player.ratings.ovr,
                      pot: player.ratings.pot,
                    });

                    return (
                      <button
                        className="btn btn-light-bordered text-start"
                        data-draft-candidate-pick-action=""
                        disabled={!usersTurn || drafting || spectator}
                        key={player.pid}
                        onClick={() => draftUser(player)}
                        type="button"
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* C2: compact face thumb on the #2/#3 rows */}
                          <PlayerFaceThumb
                            face={player.face}
                            imgURL={player.imgURL}
                            pid={player.pid}
                            size={32}
                          />
                          <div className="min-width-0 flex-grow-1">
                            <div className="d-flex justify-content-between gap-2">
                              <span className="fw-semibold">
                                #{player.rank}{" "}
                                {formatPlayerName(
                                  player.firstName,
                                  player.lastName,
                                  player.firstNameShort,
                                )}
                              </span>
                            </div>
                            <span className="small text-body-secondary d-block">
                              <PositionText pos={player.ratings.pos} /> ·{" "}
                              {i18n.t("draft.yearsOld", {
                                ns: "ui",
                                age: player.age,
                                defaultValue: "{{age}} years old",
                              })}
                            </span>
                            <div className="d-grid gap-2 mt-2">
                              <MarketRatingStatusMetric
                                className="text-start"
                                hidden={challengeNoRatings}
                                label={i18n.t("marketMetrics.playerOutlook", {
                                  ns: "ui",
                                  defaultValue: "Player outlook",
                                })}
                                potential={player.ratings.pot}
                                rating={player.ratings.ovr}
                              />
                              <MobileStatusMetric
                                className="text-start"
                                label={i18n.t("draft.risk", {
                                  ns: "ui",
                                  defaultValue: "Risk",
                                })}
                                score={getDraftRiskMetricScore(alternativeRisk)}
                                value={getRiskLabel(alternativeRisk)}
                              />
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-2">
                  <MobileP3EventFeedback event="draftReveal">
                    {usersTurn
                      ? i18n.t("draft.p3RevealUserPick", {
                          ns: "ui",
                          defaultValue: "Pick reveal ready",
                        })
                      : i18n.t("draft.p3RevealUpcomingPick", {
                          ns: "ui",
                          defaultValue: "Next pick reveal queued",
                        })}
                  </MobileP3EventFeedback>
                </div>
              </MobileContentHero>
            );
          })()
        : null}

      {/* Single overflow entry: the secondary sim/trade actions live behind this
          one disclosure while the draft is live. The sibling draft views moved
          to the phase-driven DraftPhaseNav at the top (G2d-2, was two folds),
          so this fold is only shown while there are still picks to make. */}
      {!draftIsOver && nextPick ? (
        <MobileExpertDisclosure
          className="mb-3"
          data-draft-more-menu=""
          data-v5-draft-overflow-entry=""
          expertHostId="draft-more-actions"
          summary={i18n.t("draft.moreDraftActionsViews", {
            ns: "ui",
            defaultValue: "More draft actions and views",
          })}
        >
          {!draftIsOver && nextPick ? (
            <div className="d-flex flex-wrap gap-2 mb-2" data-draft-secondary-actions="">
              <button
                className="btn btn-light-bordered btn-sm"
                disabled={drafting}
                onClick={simOnePick}
                type="button"
              >
                {i18n.t("draft.simOnePick", {
                  ns: "ui",
                  defaultValue: "Sim one pick",
                })}
              </button>
              <button
                className="btn btn-light-bordered btn-sm"
                data-draft-sim-to-pick-action=""
                disabled={drafting}
                onClick={simToCurrentPick}
                type="button"
              >
                {i18n.t("draft.simToPick", {
                  ns: "ui",
                  defaultValue: "Sim to pick",
                })}
              </button>
              {!usersTurn && userRemaining && !spectator ? (
                <button
                  className="btn btn-light-bordered btn-sm"
                  disabled={drafting}
                  onClick={simToNextUserPick}
                  type="button"
                >
                  {i18n.t("draft.toYourNextPick", {
                    ns: "ui",
                    defaultValue: "To your next pick",
                  })}
                </button>
              ) : null}
              {!fantasyDraft && !expansionDraft && !spectator ? (
                userTid !== nextPick.draft.tid ? (
                  <button
                    className="btn btn-light-bordered btn-sm"
                    disabled={drafting}
                    onClick={async () => {
                      await toWorker("actions", "tradeFor", {
                        dpid: nextPick.draft.dpid,
                        tid: nextPick.draft.tid,
                      });
                    }}
                    type="button"
                  >
                    {i18n.t("draft.tradeForPick", {
                      ns: "ui",
                      defaultValue: "Trade for pick",
                    })}
                  </button>
                ) : (
                  <a className="btn btn-light-bordered btn-sm" href={helpers.leagueUrl(["trade"])}>
                    {i18n.t("draft.tradeAwayPick", {
                      ns: "ui",
                      defaultValue: "Trade away pick",
                    })}
                  </a>
                )
              ) : null}
              {primaryAction === "finishDraft" ? null : (
                <button
                  className="btn btn-light-bordered btn-sm"
                  data-draft-ai-finish-action="secondary"
                  data-draft-ai-finish-command="untilEnd"
                  data-draft-ai-finish-confirm-required={
                    userRemaining && !spectator ? "true" : "false"
                  }
                  disabled={drafting}
                  onClick={finishDraft}
                  type="button"
                >
                  {i18n.t("draft.toEndOfDraft", {
                    ns: "ui",
                    defaultValue: "To end of draft",
                  })}
                </button>
              )}
            </div>
          ) : null}
        </MobileExpertDisclosure>
      ) : null}

      <div className="d-sm-flex gap-3">
        {messages.length > 0 ? (
          <div>
            {messages.map((message, i) => (
              <Fragment key={i}>{message}</Fragment>
            ))}
          </div>
        ) : null}

        <RosterComposition className="mb-3" players={userPlayers} />
      </div>

      {draftIsOver || prospectPreview.hiddenCount === 0 ? null : (
        // The visible top-3 already render once in the pick-decision hero above
        // (#1 expanded + #2/#3 compact rows), so this depth section only carries
        // the deeper prospect pool (rank 4+) behind the expert fold (法则二).
        <section className="mb-3">
          <MobileExpertDisclosure
            count={prospectPreview.hiddenCount}
            data-v5-draft-full-pool-expert-depth=""
            expertHostId="draft-prospect-pool"
            summary={i18n.t("draft.moreProspects", {
              ns: "ui",
              count: prospectPreview.hiddenCount,
              defaultValue: "Show {{count}} more prospects",
            })}
          >
            <ul className="list-group mt-2">
              {prospectPreview.hidden.map((player) => (
                <ProspectDecisionCard
                  challengeNoRatings={challengeNoRatings}
                  drafting={drafting}
                  draftUser={draftUser}
                  expansionDraft={expansionDraft}
                  fantasyDraft={fantasyDraft}
                  key={player.pid}
                  player={player}
                  spectator={spectator}
                  stats={stats}
                  teamNeedPositions={teamNeedSummary.positions}
                  usersTurn={usersTurn}
                />
              ))}
            </ul>
          </MobileExpertDisclosure>
        </section>
      )}

      <section className="mb-3">
        <h2 className="h5">
          {i18n.t("draft.draftResults", {
            ns: "ui",
            defaultValue: "Draft Results",
          })}
        </h2>
        {draftedPlayers.length > 0 ? (
          <>
            <ul className="list-group">
              {draftedPlayers.slice(0, draftedResultsPreviewLimit).map((player) => (
                <DraftedResultCard
                  challengeNoRatings={challengeNoRatings}
                  key={player.draft.dpid}
                  player={player}
                  season={season}
                  teamsByTid={teamsByTid}
                />
              ))}
            </ul>
            {draftedPlayers.length > draftedResultsPreviewLimit ? (
              <details className="mt-2" data-native-details-host="draft-full-results">
                <summary>
                  {i18n.t("draft.fullResults", {
                    ns: "ui",
                    defaultValue: "Full results",
                  })}
                </summary>
                <ul className="list-group mt-2">
                  {draftedPlayers.slice(draftedResultsPreviewLimit).map((player) => (
                    <DraftedResultCard
                      challengeNoRatings={challengeNoRatings}
                      key={player.draft.dpid}
                      player={player}
                      season={season}
                      teamsByTid={teamsByTid}
                    />
                  ))}
                </ul>
              </details>
            ) : null}
          </>
        ) : (
          <p className="text-body-secondary">
            {i18n.t("draft.noResultsYet", {
              ns: "ui",
              defaultValue: "No picks have been made yet.",
            })}
          </p>
        )}
      </section>
    </div>
  );
};

export default Draft;
